import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import type { Shadows } from '@mui/material/styles';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';

// Public Pages
import HomePage from './pages/public/HomePage';
import ServicesPage from './pages/public/ServicesPage';
import ServiceDetailPage from './pages/public/ServiceDetailPage';
import KooperationPage from './pages/public/KooperationPage';
import PreisePage from './pages/public/PreisePage';
import ContactPage from './pages/public/ContactPage';
import ImpressumPage from './pages/public/ImpressumPage';
import DatenschutzPage from './pages/public/DatenschutzPage';
import AgbPage from './pages/public/AgbPage';
import PowerPlatformPage from './pages/public/PowerPlatformPage';
import UeberUnsPage from './pages/public/UeberUnsPage';

// Admin Pages
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminServices from './pages/admin/AdminServices';
import AdminKooperation from './pages/admin/AdminKooperation';
import AdminPreise from './pages/admin/AdminPreise';
import AdminContacts from './pages/admin/AdminContacts';
import AdminCustomers from './pages/admin/AdminCustomers';

// Layouts
import PublicLayout from './layouts/PublicLayout';
import AdminLayout from './layouts/AdminLayout';
import ProtectedRoute from './components/ProtectedRoute';

// Theme — Squarespace-inspired: clean, minimal, editorial
const theme = createTheme({
  palette: {
    primary: {
      main: '#1737bc',
      light: '#2a4fd4',
      dark: '#0f2899',
    },
    secondary: {
      main: '#ffffff',
      light: '#f5f5f5',
      dark: '#e0e0e0',
    },
    background: {
      default: '#ffffff',
      paper: '#f7f7f7',
    },
    text: {
      primary: '#444444',
      secondary: '#6b6b6b',
    },
    divider: '#e8e8e8',
  },
  typography: {
    fontFamily: '"Plus Jakarta Sans", "Helvetica Neue", "Arial", sans-serif',
    h1: { fontWeight: 700, fontSize: '4rem', letterSpacing: '-1.5px', lineHeight: 1.1 },
    h2: { fontWeight: 700, fontSize: '2.75rem', letterSpacing: '-1px', lineHeight: 1.2 },
    h3: { fontWeight: 600, fontSize: '2rem', letterSpacing: '-0.5px', lineHeight: 1.3 },
    h4: { fontWeight: 600, fontSize: '1.5rem', letterSpacing: '-0.3px' },
    h5: { fontWeight: 600, fontSize: '1.2rem' },
    h6: { fontWeight: 500, fontSize: '1rem' },
    body1: { fontSize: '1rem', lineHeight: 1.7, color: '#444444' },
    body2: { fontSize: '0.9rem', lineHeight: 1.6 },
    overline: { fontWeight: 600, letterSpacing: '2px', fontSize: '0.75rem' },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: '0.3px' },
  },
  shape: { borderRadius: 4 },
  shadows: [
    'none',
    '0 1px 3px rgba(0,0,0,0.06)',
    '0 2px 6px rgba(0,0,0,0.08)',
    '0 4px 12px rgba(0,0,0,0.08)',
    '0 8px 24px rgba(0,0,0,0.10)',
    '0 12px 32px rgba(0,0,0,0.12)',
    ...Array(19).fill('none'),
  ] as Shadows,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          padding: '12px 28px',
          fontSize: '0.95rem',
          fontWeight: 600,
          transition: 'all 0.2s ease',
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        containedPrimary: {
          backgroundColor: '#1737bc',
          color: '#ffffff',
          '&:hover': { backgroundColor: '#0f2899', transform: 'translateY(-1px)' },
        },
        outlinedPrimary: {
          borderColor: '#1737bc',
          borderWidth: '1.5px',
          color: '#1737bc',
          '&:hover': { borderWidth: '1.5px', backgroundColor: '#1737bc', color: '#ffffff' },
        },
        outlinedSecondary: {
          borderColor: 'rgba(255,255,255,0.7)',
          borderWidth: '1.5px',
          color: '#ffffff',
          '&:hover': {
            borderWidth: '1.5px',
            backgroundColor: 'rgba(255,255,255,0.12)',
            borderColor: '#ffffff',
          },
        },
        text: {
          '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          boxShadow: 'none',
          border: '1px solid #e8e8e8',
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
          '&:hover': { borderColor: '#c0c0c0', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: { root: { boxShadow: 'none' } },
    },
    MuiChip: {
      styleOverrides: { root: { borderRadius: 3, fontWeight: 500 } },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 3,
            '& fieldset': { borderColor: '#d0d0d0' },
            '&:hover fieldset': { borderColor: '#1737bc' },
            '&.Mui-focused fieldset': { borderColor: '#1737bc', borderWidth: '1.5px' },
          },
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <LanguageProvider>
          <Router>
            <Routes>
              {/* Public Routes */}
              <Route element={<PublicLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/services" element={<ServicesPage />} />
                <Route path="/services/:id" element={<ServiceDetailPage />} />
                <Route path="/kooperation" element={<KooperationPage />} />
                <Route path="/preise" element={<PreisePage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/impressum" element={<ImpressumPage />} />
                <Route path="/datenschutz" element={<DatenschutzPage />} />
                <Route path="/agb" element={<AgbPage />} />
                <Route path="/power-platform" element={<PowerPlatformPage />} />
                <Route path="/ueber-uns" element={<UeberUnsPage />} />
              </Route>

              {/* Admin Login */}
              <Route path="/admin/login" element={<AdminLogin />} />

              {/* Admin Routes (Protected) */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<AdminDashboard />} />
                <Route path="services" element={<AdminServices />} />
                <Route path="kooperation" element={<AdminKooperation />} />
                <Route path="preise" element={<AdminPreise />} />
                <Route path="contacts" element={<AdminContacts />} />
                <Route path="customers" element={<AdminCustomers />} />
              </Route>
            </Routes>
          </Router>
        </LanguageProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
